import { formatDate, renderGps, slug } from "../lib/format.js";
import { CustomerLink, EmployeeLink } from "./recordLinks.jsx";
import { AsyncActionButton } from "./ui.jsx";

export function CustomerTable({ customers, users, openCustomer, openCall, openVisit, openOrder, openMessage, hideCustomer, requestCustomerDeletion, openEmployee }) {
  if (!customers.length) return <div className="empty">No prospects match this view.</div>;

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Type</th>
            <th>Verticals</th>
            <th>Interest</th>
            <th>Status</th>
            <th>Follow-up</th>
            <th>Owner</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => {
            const inactiveOwner = users.find((user) => user.id === customer.ownerId)?.active === false;
            return (
              <tr key={customer.id}>
	                <td>
	                  <strong>
                      <CustomerLink customer={customer} customers={customers} openCustomer={openCustomer} />
                    </strong>
                  <br />
	                  <span className="subtle">
	                    {customer.phone} · {customer.address}
	                  </span>
	                  {formatCustomFields(customer.customFields) ? (
	                    <>
	                      <br />
	                      <span className="subtle">{formatCustomFields(customer.customFields)}</span>
	                    </>
	                  ) : null}
	                </td>
                <td>{customer.type}</td>
                <td>{formatBusinessVerticals(customer)}</td>
                <td>{customer.productInterest || "-"}</td>
                <td>
                  <span className={`status ${slug(customer.status)}`}>{customer.status}</span>
                </td>
                <td>{customer.followUpDate ? formatDate(customer.followUpDate) : "-"}</td>
	                <td><EmployeeLink userId={customer.ownerId} users={users} openEmployee={openEmployee} /></td>
                <td>
                  <div className="row-actions">
                    <button className="mini-btn" onClick={() => openCustomer(customer)}>
                      Edit
                    </button>
                    <button
                      className="mini-btn"
                      disabled={inactiveOwner}
                      title={inactiveOwner ? "Reassign inactive owner before logging work" : undefined}
                      onClick={() => openCall(customer)}
                    >
                      Call
                    </button>
                    <button
                      className="mini-btn"
                      disabled={inactiveOwner}
                      title={inactiveOwner ? "Reassign inactive owner before logging work" : undefined}
                      onClick={() => openVisit(customer)}
                    >
                      Visit
                    </button>
                    {openOrder ? (
                      <button
                        className="mini-btn"
                        disabled={inactiveOwner}
                        title={inactiveOwner ? "Reassign inactive owner before creating orders" : "Create order for this prospect"}
                        onClick={() => openOrder(customer)}
                      >
                        Order
                      </button>
                    ) : null}
                    {openMessage ? (
                      <button
                        className="mini-btn"
                        disabled={inactiveOwner}
                        title={inactiveOwner ? "Reassign inactive owner before sending messages" : "Send message to this prospect"}
                        onClick={() => openMessage(customer)}
                      >
                        Message
                      </button>
                    ) : null}
                    <AsyncActionButton
                      title="Hide this prospect from your active view only"
                      pendingLabel="Deleting..."
                      errorMessage="We could not hide this prospect. Please try again."
                      onAction={() => hideCustomer(customer)}
                    >
                      Delete
                    </AsyncActionButton>
                    <AsyncActionButton
                      title="Ask an admin to permanently remove this prospect from active views"
                      pendingLabel="Requesting..."
                      errorMessage="We could not request admin removal. Please try again."
                      onAction={() => requestCustomerDeletion(customer)}
                    >
                      Request Admin Removal
                    </AsyncActionButton>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function HiddenCustomerTable({ customers, users, restoreCustomer, openCustomer, openEmployee }) {
  if (!customers.length) return null;

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Interest</th>
            <th>Status</th>
            <th>Owner</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <tr key={customer.id}>
              <td>
                <strong>
                  <CustomerLink customer={customer} customers={customers} openCustomer={openCustomer} />
                </strong>
                <br />
                <span className="subtle">
                  {customer.phone} · {customer.address}
                </span>
              </td>
              <td>{customer.productInterest || "-"}</td>
              <td>
                <span className={`status ${slug(customer.status)}`}>{customer.status}</span>
              </td>
	              <td><EmployeeLink userId={customer.ownerId} users={users} openEmployee={openEmployee} /></td>
              <td>
                <AsyncActionButton
                  pendingLabel="Restoring..."
                  errorMessage="We could not restore this prospect. Please try again."
                  onAction={() => restoreCustomer(customer)}
                >
                  Restore
                </AsyncActionButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ActivityTable({ activities, customers, users, activityCustomFields = [], openCustomer, openEmployee, openCall, openVisit, openMessage }) {
  if (!activities.length) return <div className="empty">No activities logged yet.</div>;
  const hasQuickActions = Boolean(openCall || openVisit || openMessage);

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Customer</th>
            <th>Outcome</th>
            <th>Rep</th>
            <th>GPS</th>
            <th>Summary</th>
            <th>Custom Fields</th>
            {hasQuickActions ? <th>Next Action</th> : null}
          </tr>
        </thead>
        <tbody>
          {activities.map((activity) => {
            const activityCustomer = customers.find((customer) => customer.id === activity.customerId);
            const quickActionDisabled = !activityCustomer;
            return (
              <tr key={activity.id}>
                <td>{formatDate(activity.date)}</td>
                <td>{activity.type === "call" ? "Call" : "Visit"}</td>
	                <td>
                    <CustomerLink
                      customerId={activity.customerId}
                      customers={customers}
                      fallbackName={activity.customerName || "Unknown"}
                      openCustomer={openCustomer}
                    />
                  </td>
                <td>{activity.outcome}</td>
	                <td><EmployeeLink userId={activity.userId} users={users} openEmployee={openEmployee} /></td>
                <td>{renderGps(activity)}</td>
                <td>{activity.summary || "-"}</td>
                <td>{formatActivityCustomFields(activity, activityCustomFields)}</td>
                {hasQuickActions ? (
                  <td>
                    <div className="row-actions">
                      {openCall ? (
                        <button
                          className="mini-btn"
                          type="button"
                          disabled={quickActionDisabled}
                          title={quickActionDisabled ? "This prospect is archived or hidden from this view" : `Log next call for ${activityCustomer.name}`}
                          onClick={() => openCall(activityCustomer)}
                        >
                          Next Call
                        </button>
                      ) : null}
                      {openVisit ? (
                        <button
                          className="mini-btn"
                          type="button"
                          disabled={quickActionDisabled}
                          title={quickActionDisabled ? "This prospect is archived or hidden from this view" : `Log next visit for ${activityCustomer.name}`}
                          onClick={() => openVisit(activityCustomer)}
                        >
                          Next Visit
                        </button>
                      ) : null}
                      {openMessage ? (
                        <button
                          className="mini-btn"
                          type="button"
                          disabled={quickActionDisabled}
                          title={quickActionDisabled ? "This prospect is archived or hidden from this view" : `Message ${activityCustomer.name}`}
                          onClick={() => openMessage(activityCustomer, activityMessageDraft(activity))}
                        >
                          Message
                        </button>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatActivityCustomFields(activity, definitions = []) {
  const entries = Object.entries(activity.customFields || {});
  if (!entries.length) return "-";
  const labelsByKey = new Map(definitions.map((field) => [field.key, field.label]));
  return entries
    .map(([key, value]) => `${labelsByKey.get(key) || key}: ${value}`)
    .join(" · ");
}

function activityMessageDraft(activity) {
  const activityType = activity.type === "visit" ? "visit" : "call";
  const context = activity.summary || activity.outcome || "next steps";
  return {
    channel: "WhatsApp",
    template: "Follow-up",
    message: `Following up on our ${activityType}: ${context}`,
  };
}

function formatBusinessVerticals(customer) {
  const verticals = Array.isArray(customer.businessVerticals) && customer.businessVerticals.length
    ? customer.businessVerticals
    : [customer.industryType].filter(Boolean);
  return verticals.length ? verticals.join(", ") : "-";
}

function formatCustomFields(customFields = {}) {
  const entries = Object.entries(customFields).filter(([, value]) => value);
  if (!entries.length) return "";
  return entries.map(([key, value]) => `${key}: ${value}`).join(" · ");
}
